/**
 * Represents a parsed version with its components
 */
export interface ParsedVersion {
  major: number;
  minor: number;
  patch: number;
  build: number;
  branch: string;
}

/**
 * Parses a version string into its components
 *
 * @param version - Version string in format "MAJOR.MINOR.THIRD.PATCH-BRANCH" (e.g., "2.3.35.4-main")
 * @returns ParsedVersion object with individual components
 * @throws Error if version string is invalid
 *
 * @example
 * ```ts
 * const parsed = parseVersion("2.3.35.4-main");
 * // Returns: { major: 2, minor: 3, patch: 35, build: 4, branch: "main" }
 * ```
 */
export function parseVersion(version: string): ParsedVersion {
  if (!version || typeof version !== "string") {
    throw new Error("Version must be a non-empty string");
  }

  // Split by dash to separate version numbers from branch (only first dash)
  const dashIndex = version.indexOf("-");
  if (dashIndex === -1) {
    throw new Error(
      'Version must be in format "MAJOR.MINOR.THIRD.PATCH-BRANCH"'
    );
  }

  const versionNumbers = version.substring(0, dashIndex);
  const branch = version.substring(dashIndex + 1);
  const numbers = versionNumbers.split(".");

  if (numbers.length !== 4) {
    throw new Error(
      "Version numbers must have exactly 4 parts: MAJOR.MINOR.THIRD.PATCH"
    );
  }

  const [major, minor, third, patch] = numbers.map((num) => {
    const parsed = parseInt(num, 10);
    if (isNaN(parsed) || parsed < 0) {
      throw new Error(`Invalid version number: ${num}`);
    }
    return parsed;
  });

  if (!branch.trim()) {
    throw new Error("Branch name cannot be empty");
  }

  return {
    major,
    minor,
    patch: third,
    build: patch,
    branch: branch.trim(),
  };
}

/**
 * Determines if a service worker update should be triggered based on version comparison
 *
 * Update Rules:
 * - Update if MAJOR version changes
 * - Update if MINOR version changes
 * - Update if BRANCH name changes
 * - Update if PATCH changes (when all other numbers are the same)
 * - DON'T update if only THIRD number changes (while major/minor are same)
 *
 * @param currentVersion - The current client version
 * @param serverVersion - The server version to compare against
 * @returns true if service worker should be updated, false otherwise
 *
 * @example
 * ```ts
 * // Major version change - should update
 * shouldUpdateServiceWorker("2.3.35.4-main", "3.3.35.4-main"); // true
 *
 * // Minor version change - should update
 * shouldUpdateServiceWorker("2.3.35.4-main", "2.4.35.4-main"); // true
 *
 * // Branch change - should update
 * shouldUpdateServiceWorker("2.3.35.4-main", "2.3.35.4-staging"); // true
 *
 * // Only third number change - should NOT update
 * shouldUpdateServiceWorker("2.3.35.4-main", "2.3.36.4-main"); // false
 *
 * // Patch change with same other numbers - should update
 * shouldUpdateServiceWorker("2.3.35.4-main", "2.3.35.5-main"); // true
 * ```
 */
export function shouldUpdateServiceWorker(
  currentVersion: string,
  serverVersion: string
): boolean {
  try {
    const current = parseVersion(currentVersion);
    const server = parseVersion(serverVersion);

    // Rule 1: Update if MAJOR version changes
    if (current.major !== server.major) {
      return true;
    }

    // Rule 2: Update if MINOR version changes
    if (current.minor !== server.minor) {
      return true;
    }

    // Rule 3: Update if BRANCH name changes
    if (current.branch !== server.branch) {
      return true;
    }

    // Rule 4: If major and minor are the same, check specific rules for third and patch
    if (current.major === server.major && current.minor === server.minor) {
      // Rule 5: DON'T update if only THIRD number changes
      if (current.patch !== server.patch && current.build === server.build) {
        return false;
      }

      // Rule 6: Update if PATCH changes (when everything else is the same)
      if (current.build !== server.build && current.patch === server.patch) {
        return true;
      }

      // If both third and patch changed, we need to decide
      // Since major/minor/branch are same, but both third and patch changed,
      // we follow the "don't update for third number changes" rule
      if (current.patch !== server.patch && current.build !== server.build) {
        return false;
      }
    }

    // If we get here, versions are identical
    return false;
  } catch (error) {
    // If there's an error parsing versions, log it and don't update
    console.error("Error comparing versions:", error);
    return false;
  }
}
